/**
 * Chunk RLE codec — the frozen wire/storage encoding for one 32×32 chunk
 * (_docs/tilemap/01 §3, engine plan step 45.3).
 *
 * A chunk's 1024 tile ids (row-major) are run-length-encoded as a sequence of
 * `(count, id)` pairs, each written as two little-endian u32s (8 bytes/pair),
 * then base64-encoded. Tilemaps are extremely "runny" (large same-id regions),
 * so a typical painted chunk encodes to tens of bytes.
 *
 * This exact byte layout must stay identical to `App\Support\RleCodec` (PHP)
 * and to the engine's `TilemapSerializer` — the shared fixture round-trip test
 * (`__tests__/rle.test.ts` + the PHP `RleCodecTest`) enforces it. Do not
 * "optimize" the layout without bumping the format version everywhere.
 */

import { CHUNK_AREA, EMPTY_TILE, type TileID } from './constants';

/** Bytes per RLE pair: two u32s (count, id). */
const PAIR_BYTES = 8;

/**
 * Encode exactly CHUNK_AREA tile ids into a base64 RLE string.
 *
 * @throws if `tiles.length !== CHUNK_AREA`.
 */
export function encodeChunk(tiles: ArrayLike<TileID>): string {
    if (tiles.length !== CHUNK_AREA) {
        throw new Error(`encodeChunk expects ${CHUNK_AREA} tiles, got ${tiles.length}`);
    }

    // Count runs first so the output buffer is sized exactly (no reallocation).
    let runs = 1;
    for (let i = 1; i < CHUNK_AREA; i++) {
        if (tiles[i] !== tiles[i - 1]) runs++;
    }

    const bytes = new Uint8Array(runs * PAIR_BYTES);
    const view = new DataView(bytes.buffer);
    let offset = 0;
    let runStart = 0;
    for (let i = 1; i <= CHUNK_AREA; i++) {
        if (i === CHUNK_AREA || tiles[i] !== tiles[runStart]) {
            view.setUint32(offset, i - runStart, true); // count (LE)
            view.setUint32(offset + 4, tiles[runStart] >>> 0, true); // id (LE)
            offset += PAIR_BYTES;
            runStart = i;
        }
    }

    return bytesToBase64(bytes);
}

/**
 * Decode a base64 RLE string back into a fresh Uint32Array of CHUNK_AREA ids.
 *
 * @throws if the payload is malformed (bad length, or run counts that don't
 *         sum to exactly CHUNK_AREA). Callers treat a throw as "reject this
 *         chunk" — the server does the same in RleCodec (_docs/tilemap/03 §6).
 */
export function decodeChunk(base64: string): Uint32Array {
    const bytes = base64ToBytes(base64);
    if (bytes.length === 0 || bytes.length % PAIR_BYTES !== 0) {
        throw new Error(`decodeChunk: payload length ${bytes.length} is not a positive multiple of ${PAIR_BYTES}`);
    }

    const view = new DataView(bytes.buffer, bytes.byteOffset, bytes.byteLength);
    const tiles = new Uint32Array(CHUNK_AREA);
    let cursor = 0;
    for (let offset = 0; offset < bytes.length; offset += PAIR_BYTES) {
        const count = view.getUint32(offset, true);
        const id = view.getUint32(offset + 4, true);
        if (count === 0 || cursor + count > CHUNK_AREA) {
            throw new Error(`decodeChunk: run counts exceed ${CHUNK_AREA}`);
        }
        tiles.fill(id, cursor, cursor + count);
        cursor += count;
    }
    if (cursor !== CHUNK_AREA) {
        throw new Error(`decodeChunk: run counts sum to ${cursor}, expected ${CHUNK_AREA}`);
    }
    return tiles;
}

/** Count non-empty tiles. The `used_count` denormalization (never trusted from the wire). */
export function countUsed(tiles: ArrayLike<TileID>): number {
    let n = 0;
    for (let i = 0; i < tiles.length; i++) {
        if (tiles[i] !== EMPTY_TILE) n++;
    }
    return n;
}

/** True when every tile is empty — such a chunk is deleted, never stored. */
export function isChunkEmpty(tiles: ArrayLike<TileID>): boolean {
    for (let i = 0; i < tiles.length; i++) {
        if (tiles[i] !== EMPTY_TILE) return false;
    }
    return true;
}

// --- base64 <-> bytes, working in both browser and Node/Vitest --------------

const hasBuffer = typeof Buffer !== 'undefined';

function bytesToBase64(bytes: Uint8Array): string {
    if (hasBuffer) {
        return Buffer.from(bytes).toString('base64');
    }
    let binary = '';
    for (let i = 0; i < bytes.length; i++) {
        binary += String.fromCharCode(bytes[i]);
    }
    return btoa(binary);
}

function base64ToBytes(base64: string): Uint8Array {
    if (hasBuffer) {
        const buf = Buffer.from(base64, 'base64');
        return new Uint8Array(buf.buffer, buf.byteOffset, buf.byteLength);
    }
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i++) {
        bytes[i] = binary.charCodeAt(i);
    }
    return bytes;
}
